import { selectAnatomy as parts } from '@chakra-ui/anatomy'
import type {
  PartsStyleFunction,
  PartsStyleObject,
  SystemStyleFunction,
  SystemStyleObject,
} from '@chakra-ui/theme-tools'
import { mode } from '@chakra-ui/theme-tools'
import { mergeWith } from '@chakra-ui/utils'

import { Input } from './Input'

const baseStyleField: SystemStyleFunction = (props) => {
  return {
    ...Input.baseStyle.field,
    w: '100%',
    bg: mode('neutral.50', 'gray.700')(props),
    borderRadius: 'none',
    appearance: 'none',
    cursor: 'pointer',
    paddingBottom: '1px',
    fontSize: {
      base: 'xs',
      lg: 'xs',
    },
    fontWeight: 'bold',
    lineHeight: 'input',
    '> option, > optgroup': {
      bg: mode('gray.50', 'gray.700')(props),
    },
    _disabled: {
      bg: 'neutral.50',
      _hover: {
        bg: 'neutral.50',
      },
    },
  }
}

const baseStyleIcon: SystemStyleObject = {
  width: '1.5rem',
  height: '100%',
  insetEnd: '0.5rem',
  position: 'relative',
  color: 'primary.500',
  zIndex: 1,
  _disabled: {
    color: 'neutral.300',
  },
}

const baseStyle: PartsStyleFunction<typeof parts> = (props) => ({
  field: baseStyleField(props),
  icon: baseStyleIcon,
})

const variantCustom: PartsStyleObject<typeof parts> = {
  field: {
    width: '100%',
    bg: 'select-bg',
    borderRadius: 'none',
    borderBottom: '2px solid',
    borderBottomColor: 'select-border',
    color: 'select-color',
    fontSize: 'md',
    fontWeight: 'bold',
    '> option, > optgroup': {
      color: 'select-color',
      fontWeight: 'bold',
    },
    _hover: {
      bg: 'neutral.100',
    },
    _disabled: {
      borderBottomColor: 'neutral.200',
      color: 'inherit',
      opacity: 1,
      cursor: 'not-allowed',
      _hover: {
        bg: 'neutral.50',
      },
      _invalid: {
        bgColor: 'red.50',
        borderBottomColor: 'red.500',
        _placeholder: {
          color: 'red.500',
        },
      },
    },
    _invalid: {
      bgColor: 'red.50',
      borderBottomColor: 'red.500',
      _placeholder: {
        color: 'red.500',
      },
    },
    icon: {
      color: 'select-icon-color',
      _disabled: {
        color: 'neutral.300',
      },
    },
  }
}

const variantOutline: PartsStyleFunction<typeof parts> = (props) => ({
  ...Input.variants.outline(props),
  field: {
    ...Input.variants.outline(props).field,
    fontWeight: 'medium'
  },
})

const iconSpacing = { paddingInlineEnd: '2rem' }

const sizes: Record<string, PartsStyleObject<typeof parts>> = mergeWith(
  {},
  Input.sizes,
  {
    full: {
      field: {
        width: '100%',
        px: 5,
        py: 2.5,
        ...iconSpacing,
      },
    },
    lg: {
      field: iconSpacing,
    },
    md: {
      field: {
        ...iconSpacing,
        // py: 3,
        height: 8
      },
    },
    sm: {
      field: iconSpacing,
    },
    xs: {
      field: iconSpacing,
      icon: { insetEnd: '0.25rem' },
    },
  },
)

export const Select = {
  parts: parts.keys,
  baseStyle,
  sizes,
  variants: { ...Input.variants, custom: variantCustom, outline: variantOutline },
  defaultProps: {
    ...Input.defaultProps,
    size: 'full',
    variant: 'custom',
  },
}
